'use client';

const PILLAR_ORDER = ['Profitability', 'Growth', 'Safety', 'ShareholderReturns', 'Valuation'] as const;
const PILLAR_SHORT: Record<string, string> = {
  Profitability: 'Profit',
  Growth: 'Growth',
  Safety: 'Safety',
  ShareholderReturns: 'SR',
  Valuation: 'Value',
};

interface FactorPillarsProps {
  pillars: Record<string, number>;
  stocksScored: number;
  factorCount: number;
}

function fillColor(pct: number): string {
  if (pct <= 0) return 'bg-zinc-700';
  if (pct >= 0.95) return 'bg-emerald-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]';
  return 'bg-amber-300 animate-pulse';
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function FactorPillars({ pillars, stocksScored, factorCount }: FactorPillarsProps): React.JSX.Element {
  return (
    <div className="rounded border border-zinc-800 bg-zinc-900/80 p-2">
      <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1.5">Factor pillars</div>

      <div className="flex items-end justify-between gap-1 h-[90px]">
        {PILLAR_ORDER.map((p) => {
          const pct = pillars[p] ?? 0;
          const heightPct = Math.max(2, pct * 100); // floor visible even at 0%
          return (
            <div key={p} className="flex-1 flex flex-col items-center gap-0.5">
              <div className="text-[9px] tabular-nums text-white/80">
                {(pct * 100).toFixed(0)}%
              </div>
              <div className="w-full flex-1 rounded-t-sm bg-zinc-800 overflow-hidden flex items-end">
                <div
                  className={`w-full ${fillColor(pct)} transition-[height] duration-500 ease-out`}
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <div className="text-[8px] text-white/60 uppercase tracking-wider">
                {PILLAR_SHORT[p] ?? p}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-1.5 pt-1.5 border-t border-zinc-800 text-[9px] tabular-nums text-white/60 text-center">
        {fmtCount(stocksScored)} stocks · {factorCount} factors
      </div>
    </div>
  );
}
